import { createApp } from 'vue'
import { createPinia } from 'pinia'
import App from './App.vue'
import router from './router'
import './assets/main.css'
// Импорт ради side-effect: инициализирует тему (читает localStorage,
// слушает prefers-color-scheme / Telegram WebApp themeChanged).
import './composables/useTheme'
// Импорт ради side-effect: применяет атрибут data-bg к <html> на основании
// сохранённой настройки декоративного фона (mrsimkin.jpg).
import './composables/useBackground'
// PWA service worker. Виртуальный модуль предоставляет vite-plugin-pwa.
// `registerType: 'autoUpdate'` в vite.config.ts означает, что обновление
// бандла на сервере → SW автоматически выкатит его при ближайшей
// перезагрузке вкладки. Без `prompt`-UI, чтобы не мешать.
//
// Дополнительно: ловим `onNeedRefresh` и принудительно перезагружаем
// страницу. Без этого открытая вкладка после деплоя продолжает работать
// со старым precache (старый JS-бандл, старые UI-строки вида «деактивация
// аккаунта»). С `clientsClaim()` в sw.ts новый SW активируется сразу, а
// reload подтягивает с него свежий HTML/JS.
//
// В IDE/CI у этого модуля иногда нет .d.ts (зависит от версии плагина),
// поэтому грузим динамически с try-catch — это не мешает type-check и
// не валит билд, если PWA отключён.
//
// Поверх стандартного flow добавляем активный «health-check» обновлений:
//   * `setInterval` каждые 5 минут — `swReg.update()`. Это спасает
//     долгоживущие сессии (админка в PWA на iPad), которые иначе
//     могли бы неделями работать на застывшем бандле и видеть 404
//     на «новые» эндпоинты бэка.
//   * `visibilitychange` → проверка при возвращении к вкладке. Если
//     админ свернул PWA на час и открыл снова, шанс «у нас уже деплой»
//     достаточно высокий.
import('virtual:pwa-register')
  .then(({ registerSW }) => {
    const UPDATE_INTERVAL_MS = 5 * 60 * 1000 // 5 минут
    let pendingUpdate: ServiceWorkerRegistration | null = null

    const updateSW = registerSW({
      immediate: true,
      onRegisteredSW(_swUrl, reg) {
        // Сохраняем регистрацию, чтобы дёргать `update()` руками.
        if (!reg) return
        pendingUpdate = reg

        // Периодический пинг: «выкачен ли новый бандл?»
        // workbox-window сам сравнит ETag/Content-Hash и сообщит
        // через `onNeedRefresh()` ниже, если что.
        const tick = () => {
          if (!pendingUpdate) return
          try {
            void pendingUpdate.update()
          } catch (e) {
            console.warn('periodic SW update failed', e)
          }
        }
        window.setInterval(tick, UPDATE_INTERVAL_MS)

        // При возвращении к вкладке после длительного простоя.
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') tick()
        })
      },
      onNeedRefresh() {
        // Микро-задержка, чтобы новый SW успел опубликовать активацию
        // (clientsClaim) до того, как мы релоадимся — иначе при гонке
        // старый SW может ещё раз обслужить запрос.
        setTimeout(() => {
          try {
            updateSW(true)
          } catch {
            window.location.reload()
          }
        }, 50)
      },
    })
  })
  .catch(() => {
    /* PWA disabled in this build */
  })

// Telegram WebApp может быть ещё не загружен на момент выполнения скрипта —
// подключаемся «лениво» и опционально.
if (window.Telegram?.WebApp) {
  try {
    window.Telegram.WebApp.ready()
    window.Telegram.WebApp.expand()
  } catch {
    /* noop */
  }
}

const app = createApp(App)
app.use(createPinia())
app.use(router)
app.mount('#app')
